import { zodResolver } from '@hookform/resolvers/zod'
import Accordion from '@repo/ui/accordion'
import CapsuleTab, { Tab } from '@repo/ui/capsule-tab'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useSections } from '../../../../lib/hooks/use-sections'
import useTranslate from '../../../../lib/hooks/use-translate'
import { StaffDocumentTypeEnum } from '../../../../lib/types/enums/common/document-status.enum'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import { Staff } from '../../../../lib/types/models/staff/staff'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import FormError from '../../../elements/FormError'
import { Form } from '../../../elements/forms/form'
import ValidationButtons from '../../../elements/validation-buttons'
import StaffActionsHeader from '../staff-actions-header'
import {
  accordionContentMap,
  accordionItems,
  AccordionStates,
  COMMENTS_SECTION_ID,
  getInitialAccordionStates
} from '../utils/accordion-items'
import { staffFormSchema } from '../utils/staff-form-resolver'
import StaffCommentsSection from './staff-comments-section'
import StaffContainerInformation from './staff-container-information'

const PROFESSIONAL_DETAILS_TAB_INDEX = 3
const ADMINISTRATIVE_TAB_INDEX = 4

type Props = BaseFormProps<AdminStaffInput, true> & {
  mode: 'create' | 'update'
  staffId?: number
  matricule?: string
  account?: Staff['user']
  onAccountReactivated?: () => void
}

const StaffForm = ({
  mode,
  defaultValues,
  staffId,
  matricule,
  account,
  onAccountReactivated,
  ...validationButtonProps
}: Props) => {
  const [accordionStates, setAccordionStates] = useState<AccordionStates>(() =>
    getInitialAccordionStates(mode === 'update' ? defaultValues?.position : undefined)
  )

  const tabs: Tab<'required-key'>[] = useMemo(
    () => [
      ...accordionItems.map((item) => ({
        label: accordionContentMap[item].tabText,
        key: item
      })),
      { label: 'Commentaires', key: COMMENTS_SECTION_ID }
    ],
    [accordionItems]
  )

  const { active, handleChange } = useSections({ sections: tabs })

  const { t, currentLanguage } = useTranslate()
  const form = useForm<AdminStaffInput>({
    resolver: zodResolver(staffFormSchema(currentLanguage, t)),
    defaultValues,
    mode: FORM_MODE
  })

  const handleTabChange = (index: number) => {
    handleChange(index)
    const itemKey = accordionItems[index]
    if (itemKey) setAccordionStates((prevState) => ({ ...prevState, [itemKey]: true }))
  }

  useEffect(() => {
    if (form.formState.isSubmitSuccessful && !form.formState.isLoading) form.reset(defaultValues)
  }, [defaultValues])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <StaffActionsHeader
          showActions={mode === 'update'}
          matricule={matricule}
          account={account}
          onReactivated={onAccountReactivated}
          onMissingInformationClick={() => {
            handleTabChange(ADMINISTRATIVE_TAB_INDEX)
          }}
          onMissingDocumentClick={(document) => {
            if (document.type === StaffDocumentTypeEnum.DIPLOMA)
              handleTabChange(PROFESSIONAL_DETAILS_TAB_INDEX)
            else handleTabChange(ADMINISTRATIVE_TAB_INDEX)
          }}
        />
        <div className="h-5" />
        <div className="sticky top-2 z-10">
          <CapsuleTab activeTab={active} onTabChange={handleTabChange} tabs={tabs} />
        </div>

        <div className="h-6" />
        <StaffContainerInformation />
        <div className="h-6" />

        <div className="flex flex-col gap-6">
          {accordionItems.map((itemKey) => (
            <div key={itemKey}>
              <Accordion
                id={itemKey}
                title={accordionContentMap[itemKey].accordionText}
                open={accordionStates[itemKey]}
                onToggle={() => {
                  setAccordionStates((prevState) => ({
                    ...prevState,
                    [itemKey]: !prevState[itemKey]
                  }))
                }}
              >
                {accordionContentMap[itemKey].content({ mode, staffId })}
              </Accordion>
            </div>
          ))}

          <StaffCommentsSection />
        </div>

        <ValidationButtons {...validationButtonProps} />
        <FormError />
      </form>
    </Form>
  )
}

export default StaffForm
